import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { INordicReviewBucket } from '@skystone/shared';
import { METRIC_VIEW_CHART_HEIGHT } from '@/lib/metric-view/responsive-thresholds';

/**
 * Grouped bar chart for the Nordic review-flow metrics (PRODOPSAI-678).
 *
 * Bars rather than lines to match the layout the teams already read on their
 * own goal tracker. Each bar is a team's median for the period.
 *
 * p75 is deliberately not drawn. It was rendered as an error-bar whisker and
 * removed on review: the tail runs several times the median, so the whiskers
 * dominated the plot and squashed the bars they were meant to annotate. It
 * remains on the tooltip and in the methodology text below the charts, which is
 * where the number is read rather than eyeballed.
 */

const TEAM_COLORS: Readonly<Record<string, string>> = {
  'Team Customer': 'var(--color-chart-4)',
  'Team Financial': 'var(--color-chart-8)',
  'Team Asset': 'var(--color-chart-10)',
};

const FALLBACK_COLOR = 'var(--color-chart-1)';

export interface INordicReviewChartRow {
  bucketStart: string;
  [teamName: string]: string | number | null;
}

/**
 * Pivot the flat bucket list into one row per period, one key per team.
 * p75 travels alongside its team under a suffixed key so the tooltip can show
 * both without adding a second visible series per team.
 */
export function toChartRows(buckets: INordicReviewBucket[]): INordicReviewChartRow[] {
  const rows = new Map<string, INordicReviewChartRow>();

  for (const bucket of buckets) {
    const existing = rows.get(bucket.bucketStart) ?? { bucketStart: bucket.bucketStart };
    existing[bucket.teamName] = bucket.medianHours;
    existing[`${bucket.teamName} p75`] = bucket.p75Hours;
    existing[`${bucket.teamName} count`] = bucket.prCount;
    rows.set(bucket.bucketStart, existing);
  }

  return [...rows.values()].sort((a, b) => a.bucketStart.localeCompare(b.bucketStart));
}

/**
 * Sub-hour waits read better in minutes — "18min" is concrete where "0.3h"
 * needs converting in the reader's head, and a lot of first reviews land there.
 */
export function formatHours(value: number | null): string {
  if (value === null) return '—';
  if (value < 1) return `${Math.round(value * 60)}min`;
  return `${value.toFixed(1)}h`;
}

interface ITooltipPayloadItem {
  dataKey?: string | number;
  value?: number | string | null;
  color?: string;
  payload?: INordicReviewChartRow;
}

export function ReviewTooltip({
  active,
  payload,
  label,
}: Readonly<{ active?: boolean; payload?: ITooltipPayloadItem[]; label?: string }>) {
  if (active !== true || payload === undefined || payload.length === 0) {
    return null;
  }
  const row = payload[0]?.payload;

  return (
    <div className="rounded-md border border-border bg-popover p-2 text-xs shadow-md">
      <p className="mb-1 font-medium text-foreground">{label}</p>
      {payload.map((item) => {
        const team = String(item.dataKey ?? '');
        const p75 = row?.[`${team} p75`];
        const count = row?.[`${team} count`];
        return (
          <p key={team} className="flex items-center gap-1.5" style={{ color: item.color }}>
            <span>{team}</span>
            <span className="font-medium">
              {formatHours(typeof item.value === 'number' ? item.value : null)}
            </span>
            <span className="text-muted-foreground">
              (p75 {formatHours(typeof p75 === 'number' ? p75 : null)}
              {typeof count === 'number' ? `, ${count} PRs` : ''})
            </span>
          </p>
        );
      })}
    </div>
  );
}

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * Monthly buckets are always the 1st, so "01-01" wastes width on a constant.
 * Weekly buckets keep the day, which is the part that distinguishes them.
 */
export function formatBucketTick(value: string, granularity: 'weekly' | 'monthly'): string {
  if (granularity === 'monthly') {
    const monthIndex = Number.parseInt(value.slice(5, 7), 10) - 1;
    return MONTH_LABELS[monthIndex] ?? value;
  }
  return value.slice(5);
}

interface INordicReviewBarChartProps {
  buckets: INordicReviewBucket[];
  teamNames: string[];
  /** Hidden on the calendar clock — the targets are calibrated to office hours. */
  targetHours: number | null;
  granularity?: 'weekly' | 'monthly';
  height?: number;
}

export function NordicReviewBarChart({
  buckets,
  teamNames,
  targetHours,
  granularity = 'weekly',
  height = METRIC_VIEW_CHART_HEIGHT,
}: Readonly<INordicReviewBarChartProps>) {
  const rows = toChartRows(buckets);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={rows} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
        <XAxis
          dataKey="bucketStart"
          tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
          axisLine={{ stroke: 'var(--color-border)' }}
          tickFormatter={(value: string) => formatBucketTick(value, granularity)}
        />
        <YAxis
          tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
          axisLine={{ stroke: 'var(--color-border)' }}
          // Keep the target inside the domain. When a team is comfortably under
          // it every bar is shorter than the target, and recharts would scale
          // the line out of view — hiding exactly the good news the chart is
          // there to show.
          domain={[
            0,
            (dataMax: number) =>
              Math.ceil(Math.max(dataMax, targetHours ?? 0) * 1.15),
          ]}
          // Recharts divides the domain evenly, which yields values like
          // 8.333333333333334. Left unformatted those render at full precision
          // and swamp the axis.
          tickFormatter={(value: number) =>
            Number.isInteger(value) ? String(value) : value.toFixed(1)
          }
          label={{
            value: 'hours',
            angle: -90,
            position: 'insideLeft',
            style: { fontSize: 11, fill: 'var(--color-muted-foreground)' },
          }}
        />
        <Tooltip content={<ReviewTooltip />} cursor={{ fill: 'var(--color-muted)', opacity: 0.3 }} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {targetHours !== null && (
          <ReferenceLine
            y={targetHours}
            stroke="var(--color-error)"
            strokeDasharray="4 4"
            // insideTopRight rather than right: the label sits inside the plot
            // area so it is not clipped by the container edge.
            label={{
              value: `Target < ${targetHours}h`,
              position: 'insideTopRight',
              style: { fontSize: 10, fill: 'var(--color-error)' },
            }}
          />
        )}
        {teamNames.map((team) => (
          <Bar
            key={team}
            dataKey={team}
            fill={TEAM_COLORS[team] ?? FALLBACK_COLOR}
            radius={[2, 2, 0, 0]}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
